import _ from 'lodash'
import {
	BreakpointRange,
	ClassVariant,
	HoverVariant,
	TransformData,
	VARIANTS_CSS_STRATEGIES,
} from '@wix/thunderbolt-becky-types'

import postcss from 'postcss'
import nestedPostCssPlugin from 'postcss-nested'
// @ts-ignore
import discardEmptyRulesPostCssPlugin from 'postcss-discard-empty'

import { getDisplayedId, getItemId } from '@wix/thunderbolt-commons'
import { StyleDataWithVariant } from '../carmi/variants/effectsStyle.carmi'
import { getCSSFromTransitionItem } from './transitionFunctionLibrary'
import { getCSSFromTransformationItem } from './transformationsFunctionLibrary'

type VariantCssOptions = {
	componentDefaultRotation?: number
	childrenSelectorStrategy?: string
	dataType: 'ComponentStyle' | 'TransformData' | 'TransitionData'
}

const innerConditionTypes = ['Hover', 'Class']

const getMinMax = (breakpoint: BreakpointRange) => {
	const min = breakpoint.min
	const max = breakpoint.max
	const shouldUseMin = Number.isInteger(min)
	const minPart = shouldUseMin ? `(min-width: ${min}px)` : ''
	const andPart = shouldUseMin ? ' and ' : ''
	const maxPart = Number.isInteger(max) ? `${andPart}(max-width: ${max}px)` : ''
	return `${minPart}${maxPart}`
}

const getSelectorByVariant = (variant: HoverVariant | ClassVariant | BreakpointRange) => {
	switch (variant.type) {
		case 'Hover':
			return ':hover'
		case 'Class':
			return `.${variant.id}`
		case 'BreakpointRange':
			return `@media ${getMinMax(variant)}`
		default:
			// no matching type, throwing
			// @ts-ignore
			throw new Error(`Variant condition does not exist. type: ${variant.type}`)
	}
}

const fixDisplayedOnlyCompId = (compIdToFix: string, containerId: string) => {
	const itemId = getItemId(containerId)
	if (itemId) {
		return getDisplayedId(compIdToFix, itemId)
	}
	return compIdToFix
}

const handleTransitionStrategy = (selector: string, options: VariantCssOptions) => {
	if (options.dataType === 'TransitionData') {
		const isAllChildren = options.childrenSelectorStrategy === VARIANTS_CSS_STRATEGIES.ALL_CHILDREN
		return isAllChildren ? `${selector}, ${selector} * ` : `${selector}, ${selector} > *`
	}
	return selector
}

const getCssValue = (itemWithConditions: StyleDataWithVariant, options: VariantCssOptions) =>
	itemWithConditions.type === 'TransitionData'
		? getCSSFromTransitionItem(itemWithConditions)
		: getCSSFromTransformationItem(itemWithConditions as TransformData, options.componentDefaultRotation)

const getCssWithInnerVariants = (
	targetIdToVariants: Record<string, Array<HoverVariant | ClassVariant>>,
	orderedTargetIds: Array<string>,
	cssValue: string,
	compId: string,
	options: VariantCssOptions
) => {
	// this recursion creates scss string which we then is used to generate regular css
	const getCssRec = (targetIds: Array<string>): string => {
		if (!targetIds.length) {
			return cssValue
		}
		const currentTargetId = targetIds.shift()!

		const pseudoSelector = targetIdToVariants[currentTargetId]
			.map((variant) => getSelectorByVariant(variant))
			.join('')

		let selector = `#${fixDisplayedOnlyCompId(currentTargetId, compId)}${pseudoSelector}`

		if (!targetIds.length) {
			// if none of the condition include the comp id we need to select it using #compId
			if (fixDisplayedOnlyCompId(currentTargetId, compId) !== compId) {
				selector = `${selector} #${compId}`
			}
			// if we are in Transition style we need to select all children or only direct children to be affected.
			if (options.dataType === 'TransitionData') {
				selector = handleTransitionStrategy(selector, options)
			}
			return `${selector} { ${cssValue} }`
		}

		return `${selector} { ${getCssRec(targetIds)} }`
	}

	return getCssRec(orderedTargetIds)
}

const getCssWithOuterVariants = (outerConditions: Array<BreakpointRange>, innerVariantsCss: string) => {
	const breakpointRange = outerConditions[0] // Can it be multiple conditions?
	const outerSelector = getSelectorByVariant(breakpointRange)
	return `${outerSelector} { ${innerVariantsCss} }`
}

const generateCss = (nestedCss: string): string =>
	postcss([nestedPostCssPlugin, discardEmptyRulesPostCssPlugin]).process(nestedCss, { map: false, from: undefined })
		.css

const splitConditions = (
	variants: Array<BreakpointRange | HoverVariant | ClassVariant>
): [Array<HoverVariant | ClassVariant>, Array<BreakpointRange>] =>
	// @ts-ignore
	_.partition(variants, (variant) => innerConditionTypes.includes(variant.type))

const getTargetIds = (innerConditions: Array<HoverVariant | ClassVariant>) =>
	_.chain(innerConditions)
		.map((variant) => variant.componentId)
		.uniq()
		.value()

const getInnerConditionsGroupByTarget = (innerConditions: Array<HoverVariant | ClassVariant>) =>
	innerConditions.reduce((targetIdToVariants, variant) => {
		targetIdToVariants[variant.componentId] = [...(targetIdToVariants[variant.componentId] || []), variant]
		return targetIdToVariants
	}, {} as Record<string, Array<HoverVariant | ClassVariant>>)

const getCssWithVariants = (itemWithConditions: StyleDataWithVariant, compId: string, options: VariantCssOptions) => {
	const cssValue = getCssValue(itemWithConditions, options)

	if (itemWithConditions.variants) {
		const [innerConditions, outerConditions] = splitConditions(itemWithConditions.variants)
		const targetIds: Array<string> = getTargetIds(innerConditions)
		// this grouping is needed in order to combine multiple condition on same target (#container:hover.class)
		const groupedInnerVariants = getInnerConditionsGroupByTarget(innerConditions)

		const innerVariantsCss = getCssWithInnerVariants(groupedInnerVariants, targetIds, cssValue, compId, options)

		const variantsCss = outerConditions.length
			? getCssWithOuterVariants(outerConditions, innerVariantsCss)
			: innerVariantsCss

		return generateCss(variantsCss)
	} else {
		const defaultCssSelector = handleTransitionStrategy(`#${compId}`, options)
		const defaultCss = `${defaultCssSelector} { ${cssValue} }`

		return generateCss(defaultCss)
	}
}

export { getCssWithVariants }
